// The reply's Send: what stops it and what only warns (2026-10-30, Jenn;
// docs/decisions/2026-10-30-send-warns-not-blocks.md, docs/contact-view.md
// "What can stop Send"; @cxhub/shared's send-warnings.js).
//
// sendStopReason(): the one reason Send (and Retry Send) is disabled, or
// null. Only an unfilled placeholder, a link in a Walmart reply, an empty
// reply, or no address to send to.
//
// panelSendWarnings(): every warning that applies to the reply, one list,
// each { code, text, fixes?, index? }. The CSR sees them all in
// SendWarningsDialog.jsx and can Send anyway. Another check joins by passing
// its warnings in `extra` (or by adding a field here): the dialog shows any
// list, and the acknowledgement is its codes.
import { promiseSendWarnings, orderDoneEvidence, withoutOffPathSteps, sendWarning, SEND_WARNING, SEND_FIX, mergeSendWarnings, mentionsSignifyd, SIGNIFYD_SEND_WARNING } from '@cxhub/shared'

export { mergeSendWarnings }

export const EMPTY_REPLY_REASON = "Can't send: the reply is empty."
// The regenerated compare still asking (2026-10-28; kept a stop, Jenn
// 2026-10-30): which suggestion is current, not a check of the reply.
export const REGENERATED_SEND_STOP = "Can't send: a newer draft was generated. Pick which reply to keep first."
export const RMA_REFERENCE_SEND_WARNING = "The reply's Reference ID waits for the return step's RMA number, and the return step hasn't been processed. Send anyway and the order's number goes in its place."

// "Can't send: No customer email on the order".
export function cantSend(reason) {
  if (!reason) return null
  const r = String(reason)
  return /^can['’]t send/i.test(r) ? r : `Can't send: ${r}`
}

// Whether the reply box holds nothing to send: empty, only tags and spaces.
// (A reply that is only the signature is checked by the caller with
// isSignatureOnlyReply(), which knows the signature.)
export function isBlankReply(text) {
  return !String(text ?? '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;/gi, ' ').trim()
}

// The first stop, in the order the CSR fixes them, or null.
//   placeholder: the unfilled-placeholder warning text (placeholders only;
//     the Signifyd mention is a warning since 2026-10-30);
//   walmartLink: the Walmart link warning text;
//   empty: true for an empty reply;
//   noAddress: why there is nowhere to send it.
export function sendStopReason({ placeholder = null, walmartLink = null, empty = false, noAddress = null } = {}) {
  return placeholder || walmartLink || (empty ? EMPTY_REPLY_REASON : null) || cantSend(noAddress) || null
}

// The promise warnings the panel can compute: the reply against the
// suggestion's steps, the ones processed on this screen (stepStates, plus
// processedNow: steps a Process all just ran), unchecked and marked not
// needed, and what the order shows (the suggestion's modelMeta, plus the
// refund documents a template fill read on this screen).
export function panelPromiseWarnings({ text, suggestion, stepStates = {}, processedNow = [], uncheckedSteps = [], notNeededSteps = [], refundDocuments = [] } = {}) {
  if (!suggestion) return []
  const meta = suggestion.modelMeta ?? {}
  const apiCalls = Array.isArray(meta.apiCalls) ? meta.apiCalls : []
  const processedIndexes = [
    ...Object.entries(stepStates ?? {}).filter(([, st]) => st?.status === 'success').map(([k]) => Number(k)),
    ...(processedNow ?? []).map(Number),
  ]
  return promiseSendWarnings({
    text,
    proposedActions: withoutOffPathSteps(suggestion.proposedActions ?? [], apiCalls),
    processedIndexes,
    uncheckedIndexes: uncheckedSteps ?? [],
    notNeededIndexes: notNeededSteps ?? [],
    returnLabelOnOrder: typeof meta.returnLabelOnOrder === 'boolean' ? meta.returnLabelOnOrder : null,
    evidence: orderDoneEvidence({ ...meta, apiCalls: [...apiCalls, ...(refundDocuments ?? []).filter(Boolean).map((d) => ({ refundDocument: d }))] }),
  })
}

// Every warning that applies, one list.
//   promise:        panelPromiseWarnings()'s list;
//   taskChoice:     { index, message } while the reply depends on a path or
//                   return stage not chosen (step-blocks.js), or null;
//   regenerated:    the regenerated compare's reason while it asks, or null;
//   invoiceClaim:   the "invoice is attached" warning text, or null;
//   taskSubjectMissing: true for a task email with no Subject;
//   rmaReference:   { index } while the RMA reference waits for an
//                   unprocessed return step, or null;
//   recentReply:    the double-reply guard's text, or null;
//   text:           the reply, for the Signifyd mention (a warning since
//                   2026-10-30: internal only, but Send anyway sends it);
//   extra:          any other branch's warnings, already shaped.
export function panelSendWarnings({ promise = [], taskChoice = null, regenerated = null, invoiceClaim = null, taskSubjectMissing = false, rmaReference = null, recentReply = null, text = null, extra = [] } = {}) {
  const out = []
  if (taskChoice) out.push(sendWarning(SEND_WARNING.taskPathNotChosen, `${taskChoice.message} Send anyway sends the reply as written; the choice stays unmade.`, { index: taskChoice.index, fixes: [{ kind: SEND_FIX.choosePath, label: 'Choose the path', index: taskChoice.index }] }))
  out.push(...(promise ?? []))
  if (rmaReference) out.push(sendWarning(SEND_WARNING.rmaReference, RMA_REFERENCE_SEND_WARNING, { index: rmaReference.index >= 0 ? rmaReference.index : undefined, fixes: rmaReference.index >= 0 ? [{ kind: SEND_FIX.processStep, label: 'Process the step', index: rmaReference.index }] : [] }))
  if (invoiceClaim) out.push(sendWarning(SEND_WARNING.invoiceClaim, invoiceClaim, { fixes: [{ kind: SEND_FIX.addAttachment, label: 'Add the attachment' }] }))
  if (taskSubjectMissing) out.push(sendWarning(SEND_WARNING.taskSubjectMissing, 'This email has no Subject. Enter a Subject, or Send anyway to send it with none.', { fixes: [{ kind: SEND_FIX.enterSubject, label: 'Enter a Subject' }] }))
  if (recentReply) out.push(sendWarning(SEND_WARNING.recentGroupReply, String(recentReply).replace(/\s*Send anyway\?\s*$/, '')))
  if (text && mentionsSignifyd(text)) out.push(sendWarning(SEND_WARNING.signifydMention, SIGNIFYD_SEND_WARNING))
  if (regenerated) out.push(sendWarning(SEND_WARNING.regeneratedCompare, `${regenerated} Send anyway sends the reply in the box as it is.`))
  return mergeSendWarnings(out, extra ?? [])
}

// The codes a Send anyway acknowledges.
export function warningCodes(warnings) {
  return [...new Set((warnings ?? []).map((w) => w?.code).filter(Boolean))]
}

// The dialog's list after a fix that settles a warning on the screen (Mark
// this step not needed, Check the step): the warnings about that step go.
export function withoutStepWarnings(warnings, index) {
  return (warnings ?? []).filter((w) => w?.index !== index)
}
